import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import uuid from "react-uuid";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormLabel, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import { colors } from "../helpers/colors";
import { selectLists } from "../reducers/listSlice";
import { addProject } from "../reducers/projectSlice";
import { selectUser } from "../reducers/userSlice";
import { ProjectListsTransferList } from "./ProjectListsTransferList";
import { Info } from "@mui/icons-material";
import { useSnackbar } from "notistack";

export const EditProject = ({ project, handleEditProjectClose }) => {
  
  const [projectName, setProjectName] = useState(project?.name || "New Project");
  const [projectDescription, setProjectDescription] = useState(project?.description || project?.name || "New Project");
  const [projectLists, setProjectLists] = useState(project?.listIds || []);

  const user = useSelector(selectUser);
  const lists = useSelector(selectLists);
  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const [visibility, setVisibility] = useState(project?.public ? 'public' : 'private');

  return (
    <Dialog 
      open={true} 
      onClose={handleEditProjectClose}
      fullWidth={true}
      maxWidth="md"
    >
      <DialogTitle id="form-dialog-title">{project ? 'Edit' : 'New'} Project</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="name"
          label="Project Name"
          placeholder="Project Name"
          type="text"
          fullWidth
          value={projectName}
          variant="outlined"
          onChange={e => setProjectName(e.target.value)}
        />

        <TextField
          autoFocus
          margin="dense"
          id="description"
          label="Project Description"
          placeholder="Project Description"
          type="text"
          fullWidth
          multiline
          value={projectDescription}
          variant="outlined"
          style={{marginTop: 20}}
          onChange={e => setProjectDescription(e.target.value)}
        />
        
        <FormControl component="fieldset" style={{width: '100%', marginTop: 20}}>
          <FormLabel component="legend">Visibility and Access</FormLabel>
          <ToggleButtonGroup
            value={visibility}
            exclusive
            onChange={updateVisibility}
            style={{width: '100%'}}
          >
            <ToggleButton value="private" style={{width: '50%'}} color="primary">
              Private
            </ToggleButton>
            <ToggleButton value="public" style={{width: '50%'}} color="primary">
              Public
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        <div style={{display: 'flex', alignItems: 'center', margin: '10px 10px 20px 10px'}}>
          <Info color="primary" /><Typography component="span" style={{marginLeft: 10}}>
            {visibility === 'public' ?
              'Public projects will be visible to all users within your organization.' :
              'Private projects will only be visible to you.'
            }
          </Typography>
        </div>

        <div style={{marginTop: 20}}>
          <Typography variant="h6">Add lists</Typography>

          <ProjectListsTransferList
            allLists={lists?.allIds} 
            listIds={projectLists} 
            setListIds={setProjectLists} 
          />
        </div>

      </DialogContent>
      <DialogActions>
        <Button onClick={handleEditProjectClose} color="primary">
          Cancel
        </Button>
        <Button onClick={() => handleNewProjectCreate(user.value.email)} variant="contained" color="primary" disabled={projectName.length === 0}>
          {project ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  function updateVisibility(e, newValue) {
    newValue && setVisibility(newValue);
  };

  function addProjectClick(email) {
    try {
      const id = project ? project.id : uuid();
      const color = project ? project.color : colors[Math.floor(Math.random() * colors.length - 1)];
      const newProject = {
        id,
        name: projectName, 
        color,
        description: projectDescription,
        createdBy: email,
        createdOn: new Date().toLocaleString(),
        lastModified: new Date().toLocaleString(),
        listIds: projectLists || [],
        currentListId: projectLists?.[0] || "",
        economicsIds: project ? project.economicsIds : [],
        fbaIds: project ? project.fbaIds : [],
        public: visibility === 'public'
      };

      const newLists = projectLists?.length > 0 ? projectLists.map(listId => {
        const currentList = lists.byId[listId];
        return {
          id: listId,
          name: currentList?.name,
          createdBy: currentList?.createdBy,
          lastModified: new Date(),
          color: currentList?.color,
          description: currentList?.description,
          wellUwis: currentList?.wellUwis,
          typeCurveId: currentList?.typeCurveId,
          groupWellId: currentList?.groupWellId,
          forecastListTab: currentList?.forecastListTab,
          filters: currentList?.filters
        };
      }) : [];

      dispatch(addProject({ project: newProject, lists: newLists, email: user?.value?.email })).then(() => {
        enqueueSnackbar(`Project ${newProject?.name} ${project ? 'saved' : 'created'} successfully`, { variant: 'success' });
      });

    } catch (error) {
      console.log("Could not create project");
      console.log(error);
    };
  };
  
  function handleNewProjectCreate(email) {
    handleEditProjectClose();
    addProjectClick(email);
  };

};